import { useEffect, useState, type ReactNode } from 'react';
import { resumeRemembered, vaultStatus } from '@/services/appSession';
import { clearTokens } from '@/platform/googleAuth';
import { onDbBlocked } from '@/data/db/schema';
import { useSession } from '../state/session';
import { LoginScreen } from '../screens/LoginScreen';
import { useAutoLock } from './useAutoLock';

/** 잠금 해제 전에는 어떤 상담 화면도 렌더링하지 않는다 (SYNC_SECURITY §6·§7) */
export function VaultGate({ children }: { children: ReactNode }) {
  const session = useSession((s) => s.session);
  const [status, setStatus] = useState<'loading' | 'uninitialized' | 'locked'>('loading');
  const [error, setError] = useState<string | null>(null);

  const [slow, setSlow] = useState(false);
  const [blocked, setBlocked] = useState(false);
  useEffect(() => onDbBlocked(() => setBlocked(true)), []);

  useEffect(() => {
    if (session) return;
    clearTokens(); // 잠기면 Google 토큰도 메모리에서 지운다
    const t = window.setTimeout(() => setSlow(true), 5000);
    vaultStatus()
      .then(async (st) => {
        // 이 기기에서 로그인 유지 중이면 비밀번호 없이 바로 연다
        if (st === 'locked') {
          const r = await resumeRemembered();
          if (r) {
            const s = useSession.getState();
            if (r.accountName) s.setAccountName(r.accountName);
            s.setRemembered(true);
            s.setSession(r.session);
            return;
          }
        }
        setStatus(st);
      })
      .finally(() => window.clearTimeout(t))
      .catch(() => setError('로컬 저장소를 열 수 없습니다. 브라우저의 저장소 설정(사이트 데이터 허용)을 확인하세요.'));
  }, [session]);

  const remembered = useSession((s) => s.remembered);
  useAutoLock(!!session && !remembered);

  if (session) return <>{children}</>;
  if (error) return <p role="alert">{error}</p>;
  if (status === 'loading')
    return (
      <div className="center">
        <p className="muted">불러오는 중…</p>
        {(blocked || slow) && (
          <p className="warn small">
            이전 버전의 앱이 다른 탭이나 백그라운드 앱으로 열려 있어 기다리는 중일 수 있습니다. 그 탭·앱을 모두
            닫은 뒤 이 화면을 새로고침하세요. 데이터는 안전합니다.
          </p>
        )}
      </div>
    );
  return <LoginScreen hasVault={status === 'locked'} />;
}
